import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from 'styled-components';
import { get, patch } from "../../../../../utils/api";

const Container = styled.div`
  padding: 20px;
  background: #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  &:focus {
    border-color: #007bff;
    outline: none;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const Button = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  &:hover {
    background-color: #0056b3;
  }
`;

interface FormData {
  collection_name: string;
  primary_key: string;
  sequence_value: number;
  crud_api: boolean;
}

const GeneralSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    collection_name: "",
    primary_key: "",
    sequence_value: 0,
    crud_api: true,
  });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (id) {
      getFieldSettings(id);
    }
  }, [id]);

  const getFieldSettings = async (fieldSettingId: string) => {
    try {
      const response = await get(`field_settings/${fieldSettingId}`,{});
      if (response && response.data) {
        setFormData({
          collection_name: response.data.collection_name,
          primary_key: response.data.primary_key,
          sequence_value: response.data.sequence_value || 0,
          crud_api: response.data.crud_api !== undefined ? response.data.crud_api : true,
        });
      }
    } catch (error) {
      console.error("Failed to fetch field setting data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSave = async () => {
    try {
      await patch(`field_settings/${id}`, formData);
      navigate("/admin/field-settings/fields");
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Container>
      <FormGroup>
        <Label htmlFor="collectionName">Collection Name</Label>
        <Input
          type="text"
          id="collectionName"
          name="collection_name"
          value={formData.collection_name}
          onChange={handleFormChange}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="primaryKey">Primary Key</Label>
        <Input
          type="text"
          id="primaryKey"
          name="primary_key"
          value={formData.primary_key}
          onChange={handleFormChange}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="sequenceValue">Sequence Value</Label>
        <Input
          type="number"
          id="sequenceValue"
          name="sequence_value"
          value={formData.sequence_value.toString()}
          onChange={handleFormChange}
          required
        />
      </FormGroup>

      <SwitchContainer>
        <Label htmlFor="crudApi">CRUD API Enabled</Label>
        <Input
          type="checkbox"
          checked={formData.crud_api}
          onChange={handleFormChange}
          name="crud_api"
        />
      </SwitchContainer>

      <Button onClick={handleSave}>
        Save and Continue
      </Button>
    </Container>
  );
};

export default GeneralSettings;
